import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Switch,
  FormControlLabel,
  Container,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  Avatar,
} from '@mui/material';
import axios from 'axios';
import API_BASE from '../apiBase';
import { getStoredUser, setStoredUser } from '../utils/userStorage';

function Settings() {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true); // Temporarily disable all fields and buttons
  
  // Default configuration values
  const [defaultConfig, setDefaultConfig] = useState({
    prCycleTime: {
      startPoint: 'PR Opened',
      endPoint: 'PR Merged to Main/Master'
    },
    jiraCycleTime: {
      startStatus: 'In Progress',
      endStatus: 'Done'
    },
    additionalConfig: {
      includeWeekends: false,
      excludeBlockedTime: true,
      aggregateMultiplePRs: true
    }
  });

  // Cycle time options from backend
  const [cycleTimeOptions, setCycleTimeOptions] = useState({
    prStartPoints: ['PR Opened', 'PR Created', 'PR Submitted', 'PR Assigned', 'PR In Review'],
    prEndPoints: ['PR Merged to Main/Master', 'PR Merged to Develop', 'PR Closed', 'PR Approved', 'PR Deployed to Production'],
    jiraStartStatuses: ['To Do', 'In Progress', 'Assigned', 'Open', 'Ready for Development'],
    jiraEndStatuses: ['Done', 'Completed', 'Closed', 'Resolved', 'Deployed']
  });
  
  // Cycle time configuration state
  const [cycleTimeConfig, setCycleTimeConfig] = useState(defaultConfig);
  
  // Comment preferences state
  const [commentPreferences, setCommentPreferences] = useState({
    critical: true,  // Enabled by default, uneditable
    medium: true,   // Enabled by default
    low: false      // Disabled by default
  });
  
  // Check if current config differs from default
  const hasChanges = JSON.stringify(cycleTimeConfig) !== JSON.stringify(defaultConfig);

  // Load user settings and options on component mount
  useEffect(() => {
    loadCycleTimeOptions();
    loadUserSettings();
  }, []);

  const loadCycleTimeOptions = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/cycle-time-options`);
      if (response.data) {
        setCycleTimeOptions({
          prStartPoints: response.data.pr_start_points || cycleTimeOptions.prStartPoints,
          prEndPoints: response.data.pr_end_points || cycleTimeOptions.prEndPoints,
          jiraStartStatuses: response.data.jira_start_statuses || cycleTimeOptions.jiraStartStatuses,
          jiraEndStatuses: response.data.jira_end_statuses || cycleTimeOptions.jiraEndStatuses
        });
      }
    } catch (error) {
      console.error('Failed to load cycle time options:', error);
      // Keep default options if loading fails
    }
  };

  const resolveUserEmail = () => {
    const stored = getStoredUser();
    if (stored?.email) {
      return stored.email;
    }
    try {
      const sessionEmail = sessionStorage.getItem('userEmail');
      if (sessionEmail) {
        return sessionEmail;
      }
      const legacyEmail = localStorage.getItem('userEmail');
      if (legacyEmail) {
        sessionStorage.setItem('userEmail', legacyEmail);
        localStorage.removeItem('userEmail');
        return legacyEmail;
      }
    } catch {
      // ignore storage errors
    }
    return 'demo@example.com';
  };

  const loadUserSettings = async () => {
    setLoading(true);
    try {
      const userEmail = resolveUserEmail();
      const response = await axios.get(`${API_BASE}/api/user-settings?user_email=${encodeURIComponent(userEmail)}`);
      
      if (response.data && response.data.cycle_time_config) {
        const userConfig = response.data.cycle_time_config;
        // Ensure the config has the expected structure
        const validatedConfig = {
          prCycleTime: {
            startPoint: userConfig.prCycleTime?.startPoint || defaultConfig.prCycleTime.startPoint,
            endPoint: userConfig.prCycleTime?.endPoint || defaultConfig.prCycleTime.endPoint
          },
          jiraCycleTime: {
            startStatus: userConfig.jiraCycleTime?.startStatus || defaultConfig.jiraCycleTime.startStatus,
            endStatus: userConfig.jiraCycleTime?.endStatus || defaultConfig.jiraCycleTime.endStatus
          },
          additionalConfig: {
            includeWeekends: userConfig.additionalConfig?.includeWeekends ?? defaultConfig.additionalConfig.includeWeekends,
            excludeBlockedTime: userConfig.additionalConfig?.excludeBlockedTime ?? defaultConfig.additionalConfig.excludeBlockedTime,
            aggregateMultiplePRs: userConfig.additionalConfig?.aggregateMultiplePRs ?? defaultConfig.additionalConfig.aggregateMultiplePRs
          }
        };
        setCycleTimeConfig(validatedConfig);
        setDefaultConfig(validatedConfig);
      } else {
        // Use default config if no user settings found
        setCycleTimeConfig(defaultConfig);
        setDefaultConfig(defaultConfig);
      }
    } catch (error) {
      console.error('Failed to load user settings:', error);
      // Keep default settings if loading fails
      if (error.response?.status === 404) {
        console.log('User settings not found, using defaults');
      }
      // Ensure we always have a valid config
      setCycleTimeConfig(defaultConfig);
      setDefaultConfig(defaultConfig);
    }
    setLoading(false);
  };

  // Cycle time configuration handlers
  const handleCycleTimeChange = (section, key) => (event) => {
    setCycleTimeConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: event.target.value
      }
    }));
  };

  const handleAdditionalConfigChange = (key) => (event) => {
    setCycleTimeConfig(prev => ({
        ...prev,
      additionalConfig: {
        ...prev.additionalConfig,
            [key]: event.target.checked
        }
    }));
  };

  const handleCommentPreferenceChange = (severity) => (event) => {
    // Critical is always enabled and uneditable
    if (severity === 'critical') return;
    
    setCommentPreferences(prev => ({
      ...prev,
      [severity]: event.target.checked
    }));
  };
  
  const handleResetToDefault = () => {
    setCycleTimeConfig(defaultConfig);
    setSuccess('Cycle time configuration reset to defaults');
  };

  const handleSaveCycleTimeConfig = async () => {
    setSuccess('');
    setError('');
    try {
      const userEmail = resolveUserEmail();
      
      // Save the cycle time configuration to MongoDB
      await axios.put(`${API_BASE}/api/user-cycle-time-config?user_email=${encodeURIComponent(userEmail)}`, cycleTimeConfig);
      
      setSuccess('Cycle time configuration saved successfully');
      
      // After successful save, update the default config to current values
      // This will make hasChanges = false until next modification
      setDefaultConfig(cycleTimeConfig);
    } catch (error) {
      console.error('Failed to save cycle time configuration:', error);
      setError('Failed to save cycle time configuration');
    }
  };


  if (loading) {
    return (
        <Container sx={{ maxWidth: { lg: "1000px", xl: "1200px" } }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <Typography variant="h6" sx={{ color: '#737373' }}>
            Loading settings...
          </Typography>
      </Box>
      </Container>
    );
  }

  // Safety check to ensure cycleTimeConfig is properly initialized
  if (!cycleTimeConfig || !cycleTimeConfig.prCycleTime || !cycleTimeConfig.jiraCycleTime || !cycleTimeConfig.additionalConfig) {
    return (
        <Container sx={{ maxWidth: { lg: "1000px", xl: "1200px" } }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <Typography variant="h6" sx={{ color: '#737373' }}>
            Initializing settings...
          </Typography>
      </Box>
      </Container>
    );
  }

  return (
    <Container sx={{ maxWidth: { lg: "1000px", xl: "1200px" } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 6, mt: 2 }}>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 800, color: '#1F2937' }}>
            Settings
        </Typography>
            {hasChanges && (
                <Chip 
                    label="Unsaved Changes" 
                    size="small" 
                    sx={{ 
                        backgroundColor: '#FEF3C7', 
                        color: '#92400E',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        border: '1px solid #F59E0B'
                    }} 
                />
            )}
        </Box>

        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 4, borderRadius: 12 }}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 4, borderRadius: 12 }}>{success}</Alert>}

        <Grid container spacing={4}>

            {/* Comment Preferences */}
            <Grid item xs={12}>
                <Card sx={{ 
                    borderRadius: 3,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)',
                    border: '1px solid #E5E7EB',
                    overflow: 'hidden',
                    backgroundColor: '#FFFFFF'
                }}>
                    <CardHeader 
                        title="Comments" 
                        subheader="Control which comment severities appear in your code reviews. Critical comments are always included to ensure code quality and security."
                        titleTypographyProps={{ fontWeight: 700, fontSize: '1.25rem', color: '#1F2937' }}
                        subheaderTypographyProps={{ fontSize: '0.95rem', color: '#6B7280' }}
                        sx={{ pb: 1, backgroundColor: '#F9FAFB' }}
                    />
                    <Divider sx={{ borderColor: '#E5E7EB' }} />
                    <CardContent sx={{ p: 4 }}>
                        <Grid container spacing={3}>
                            {/* Critical Severity */}
                            <Grid item xs={12}>
                                <FormControlLabel 
                                    control={
                                        <Switch 
                                            checked={commentPreferences.critical}
                                            onChange={handleCommentPreferenceChange('critical')}
                                            disabled={true}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: '#EF4444',
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: '#EF4444',
                                                },
                                                '& .MuiSwitch-track': {
                                                    backgroundColor: '#EF4444',
                                                },
                                            }}
                                        />
                                    } 
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ fontWeight: 600, color: '#1F2937' }}>
                                                Critical
                                            </Typography>
                                            <Chip 
                                                label="Required" 
                                                size="small" 
                                                sx={{ 
                                                    backgroundColor: '#FEE2E2', 
                                                    color: '#991B1B',
                                                    fontWeight: 600,
                                                    fontSize: '0.7rem',
                                                    height: 20
                                                }} 
                                            />
                                        </Box>
                                    }
                                />
                            </Grid>
                            
                            {/* Medium Severity */}
                            <Grid item xs={12}>
                                <FormControlLabel 
                                    control={
                                        <Switch 
                                            checked={commentPreferences.medium}
                                            onChange={handleCommentPreferenceChange('medium')}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: '#F59E0B',
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: '#F59E0B',
                                                },
                                                '& .MuiSwitch-track': {
                                                    backgroundColor: '#D1D5DB',
                                                },
                                            }}
                                        />
                                    } 
                                    label={
                                        <Typography sx={{ fontWeight: 600, color: '#1F2937' }}>
                                            Medium
                                        </Typography>
                                    }
                                />
                            </Grid>
                            
                            {/* Low Severity */}
                            <Grid item xs={12}>
                                <FormControlLabel 
                                    control={
                                        <Switch 
                                            checked={commentPreferences.low}
                                            onChange={handleCommentPreferenceChange('low')}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: '#10B981',
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: '#10B981',
                                                },
                                                '& .MuiSwitch-track': {
                                                    backgroundColor: '#D1D5DB',
                                                },
                                            }}
                                        />
                                    } 
                                    label={
                                        <Typography sx={{ fontWeight: 600, color: '#1F2937' }}>
                                            Low
                                        </Typography>
                                    }
                                />
                            </Grid>
                        </Grid>

                        {/* Update Button */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                          <Button
                            variant="contained"
                            onClick={async () => {
                              setSuccess('');
                              setError('');
                              try {
                                // Always enforce critical true
                                const payload = { critical: true, medium: commentPreferences.medium, low: commentPreferences.low };
                                await axios.put(`${API_BASE}/api/projects/control-preferences`, payload);
                                setSuccess('Comment preferences updated for your projects');
                                // Update local user cache if present
                                const storedUser = getStoredUser();
                                if (storedUser) {
                                  storedUser.comment_preference = payload;
                                  setStoredUser(storedUser);
                                }
                              } catch (err) {
                                console.error('Failed to update comment preferences:', err);
                                setError('Failed to update comment preferences');
                              }
                            }}
                            sx={{
                              borderRadius: 2,
                              px: 4,
                              py: 1.5,
                              fontWeight: 600,
                              fontSize: '1rem',
                              backgroundColor: '#2563EB',
                              color: '#FFFFFF',
                              boxShadow: '0 1px 2px rgba(37, 99, 235, 0.2)',
                              '&:hover': {
                                backgroundColor: '#1D4ED8',
                                boxShadow: '0 4px 6px rgba(37, 99, 235, 0.3)'
                              }
                            }}
                          >
                            Update All Projects
                          </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

            {/* Pull Request Cycle Time Configuration */}
            <Grid item xs={12}>
                <Card sx={{ 
                    borderRadius: 3,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)',
                    border: '1px solid #E5E7EB',
                    overflow: 'hidden',
                    backgroundColor: isDisabled ? '#F9FAFB' : '#FFFFFF',
                    opacity: isDisabled ? 0.7 : 1
                }}>
                    <CardHeader 
                        title="Pull Request Cycle Time" 
                        subheader="Define how you measure PR cycle time in your development workflow."
                        titleTypographyProps={{ fontWeight: 700, fontSize: '1.25rem', color: '#1F2937' }}
                        subheaderTypographyProps={{ fontSize: '0.95rem', color: '#6B7280' }}
                        sx={{ pb: 1, backgroundColor: '#F9FAFB' }}
                    />
                    <Divider sx={{ borderColor: '#E5E7EB' }} />
                    <CardContent sx={{ p: 4 }}>
                        <Grid container spacing={4}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth sx={{ mb: 3 }}>
                                    <InputLabel sx={{ fontWeight: 600, color: '#374151' }}>Start Point</InputLabel>
                                    <Select
                                        value={cycleTimeConfig.prCycleTime.startPoint}
                                        onChange={handleCycleTimeChange('prCycleTime', 'startPoint')}
                                        label="Start Point"
                                        disabled={isDisabled}
                            sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                backgroundColor: '#FFFFFF',
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#2563EB',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#2563EB',
                                                    borderWidth: 2,
                                                },
                                            },
                                            '& .MuiSelect-select': {
                                                color: '#1F2937',
                                            },
                                        }}
                                    >
                                        {cycleTimeOptions.prStartPoints.map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth sx={{ mb: 3 }}>
                                    <InputLabel sx={{ fontWeight: 600, color: '#374151' }}>End Point</InputLabel>
                                    <Select
                                        value={cycleTimeConfig.prCycleTime.endPoint}
                                        onChange={handleCycleTimeChange('prCycleTime', 'endPoint')}
                                        label="End Point"
                                        disabled={isDisabled}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                backgroundColor: '#FFFFFF',
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#2563EB',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#2563EB',
                                                    borderWidth: 2,
                                                },
                                            },
                                            '& .MuiSelect-select': {
                                                color: '#1F2937',
                                            },
                                        }}
                                    >
                                        {cycleTimeOptions.prEndPoints.map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                        
                        {/* Timeline Preview */}
                        <Box sx={{ mt: 4, p: 3, backgroundColor: '#F9FAFB', borderRadius: 2, border: '1px solid #E5E7EB' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1F2937' }}>
                                Timeline Preview
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {/* Start Point */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                                    <Box sx={{ 
                                        width: 40, 
                                        height: 40, 
                                        borderRadius: '50%', 
                                        backgroundColor: '#10B981',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '3px solid #FFFFFF',
                                        boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
                                    }}>
                                        <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '0.875rem' }}>
                                            1
                                        </Typography>
                                    </Box>
                                    <Paper sx={{ 
                                        p: 2, 
                                        backgroundColor: '#ECFDF5', 
                                        border: '1px solid #10B981', 
                                        flex: 1,
                                        borderRadius: 2,
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                    }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#065F46' }}>
                                            {cycleTimeConfig.prCycleTime.startPoint}
                                        </Typography>
                                    </Paper>
                                </Box>
                                
                                {/* Connection Line */}
                                <Box sx={{ 
                                    flex: 0.5, 
                                    height: 2, 
                                    backgroundColor: '#D1D5DB',
                                    position: 'relative',
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        right: -6,
                                        top: -4,
                                        width: 0,
                                        height: 0,
                                        borderLeft: '8px solid #D1D5DB',
                                        borderTop: '5px solid transparent',
                                        borderBottom: '5px solid transparent',
                                    }
                                }} />
                                
                                {/* End Point */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                                    <Box sx={{ 
                                        width: 40, 
                                        height: 40, 
                                        borderRadius: '50%', 
                                        backgroundColor: '#2563EB',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '3px solid #FFFFFF',
                                        boxShadow: '0 2px 4px rgba(37, 99, 235, 0.3)'
                                    }}>
                                        <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '0.875rem' }}>
                                            2
                                        </Typography>
                                    </Box>
                                    <Paper sx={{ 
                                        p: 2, 
                                        backgroundColor: '#EFF6FF', 
                                        border: '1px solid #2563EB', 
                                        flex: 1,
                                        borderRadius: 2,
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                    }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E40AF' }}>
                                            {cycleTimeConfig.prCycleTime.endPoint}
                        </Typography>
                                    </Paper>
                                </Box>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

            {/* JIRA Cycle Time Configuration */}
            <Grid item xs={12}>
                <Card sx={{ 
                    borderRadius: 3,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)',
                    border: '1px solid #E5E7EB',
                    overflow: 'hidden',
                    backgroundColor: isDisabled ? '#F9FAFB' : '#FFFFFF',
                    opacity: isDisabled ? 0.7 : 1
                }}>
                     <CardHeader 
                        title="JIRA Cycle Time" 
                        subheader="Select which JIRA statuses define the start and end of your ticket cycle."
                        titleTypographyProps={{ fontWeight: 700, fontSize: '1.25rem', color: '#1F2937' }}
                        subheaderTypographyProps={{ fontSize: '0.95rem', color: '#6B7280' }}
                        sx={{ pb: 1, backgroundColor: '#F9FAFB' }}
                    />
                    <Divider sx={{ borderColor: '#E5E7EB' }} />
                     <CardContent sx={{ p: 4 }}>
                        <Grid container spacing={4}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth sx={{ mb: 3 }}>
                                    <InputLabel sx={{ fontWeight: 600, color: '#374151' }}>Start Status</InputLabel>
                                    <Select
                                        value={cycleTimeConfig.jiraCycleTime.startStatus}
                                        onChange={handleCycleTimeChange('jiraCycleTime', 'startStatus')}
                                        label="Start Status"
                                        disabled={isDisabled}
                                            sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                backgroundColor: '#FFFFFF',
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#2563EB',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#2563EB',
                                                    borderWidth: 2,
                                                },
                                            },
                                            '& .MuiSelect-select': {
                                                color: '#1F2937',
                                            },
                                        }}
                                    >
                                        {cycleTimeOptions.jiraStartStatuses.map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth sx={{ mb: 3 }}>
                                    <InputLabel sx={{ fontWeight: 600, color: '#374151' }}>End Status</InputLabel>
                                    <Select
                                        value={cycleTimeConfig.jiraCycleTime.endStatus}
                                        onChange={handleCycleTimeChange('jiraCycleTime', 'endStatus')}
                                        label="End Status"
                                        disabled={isDisabled}
                                            sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                backgroundColor: '#FFFFFF',
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#2563EB',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#2563EB',
                                                    borderWidth: 2,
                                                },
                                            },
                                            '& .MuiSelect-select': {
                                                color: '#1F2937',
                                            },
                                        }}
                                    >
                                        {cycleTimeOptions.jiraEndStatuses.map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                        
                        {/* Workflow Preview */}
                        <Box sx={{ mt: 4, p: 3, backgroundColor: '#F9FAFB', borderRadius: 2, border: '1px solid #E5E7EB' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1F2937' }}>
                                Workflow Preview
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                                {cycleTimeOptions.jiraStartStatuses.map((status, index) => (
                                    <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Chip 
                                            label={status}
                                            sx={{
                                                backgroundColor: cycleTimeConfig.jiraCycleTime.startStatus === status ? '#2563EB' : '#F3F4F6',
                                                color: cycleTimeConfig.jiraCycleTime.startStatus === status ? '#FFFFFF' : '#6B7280',
                                                fontWeight: cycleTimeConfig.jiraCycleTime.startStatus === status ? 600 : 500,
                                                borderRadius: 3,
                                                height: 32,
                                                '&:hover': {
                                                    backgroundColor: cycleTimeConfig.jiraCycleTime.startStatus === status ? '#1D4ED8' : '#E5E7EB',
                                                },
                                                border: cycleTimeConfig.jiraCycleTime.startStatus === status ? 'none' : '1px solid #D1D5DB',
                                            }}
                                        />
                                        {index < cycleTimeOptions.jiraStartStatuses.length - 1 && (
                                            <Typography sx={{ color: '#9CA3AF', fontSize: '0.875rem' }}>→</Typography>
                                        )}
                                    </Box>
                                ))}
                                <Typography sx={{ color: '#9CA3AF', fontSize: '0.875rem', mx: 1 }}>→</Typography>
                                {cycleTimeOptions.jiraEndStatuses.map((status, index) => (
                                    <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Chip 
                                            label={status}
                                            sx={{
                                                backgroundColor: cycleTimeConfig.jiraCycleTime.endStatus === status ? '#10B981' : '#F3F4F6',
                                                color: cycleTimeConfig.jiraCycleTime.endStatus === status ? '#FFFFFF' : '#6B7280',
                                                fontWeight: cycleTimeConfig.jiraCycleTime.endStatus === status ? 600 : 500,
                                                borderRadius: 3,
                                                height: 32,
                                                '&:hover': {
                                                    backgroundColor: cycleTimeConfig.jiraCycleTime.endStatus === status ? '#059669' : '#E5E7EB',
                                                },
                                                border: cycleTimeConfig.jiraCycleTime.endStatus === status ? 'none' : '1px solid #D1D5DB',
                                            }}
                                        />
                                        {index < cycleTimeOptions.jiraEndStatuses.length - 1 && (
                                            <Typography sx={{ color: '#9CA3AF', fontSize: '0.875rem' }}>→</Typography>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                     </CardContent>
                </Card>
            </Grid>

            {/* Additional Configuration */}
             <Grid item xs={12}>
                <Card sx={{ 
                    borderRadius: 3,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)',
                    border: '1px solid #E5E7EB',
                    overflow: 'hidden',
                    backgroundColor: isDisabled ? '#F9FAFB' : '#FFFFFF',
                    opacity: isDisabled ? 0.7 : 1
                }}>
                    <CardHeader 
                        title="Additional Configuration" 
                        subheader="Fine-tune how cycle time is calculated across both PRs and JIRA tickets."
                        titleTypographyProps={{ fontWeight: 700, fontSize: '1.25rem', color: '#1F2937' }}
                        subheaderTypographyProps={{ fontSize: '0.95rem', color: '#6B7280' }}
                        sx={{ pb: 1, backgroundColor: '#F9FAFB' }}
                    />
                    <Divider sx={{ borderColor: '#E5E7EB' }} />
                    <CardContent sx={{ p: 4 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel 
                                    control={
                                        <Switch 
                                            checked={cycleTimeConfig.additionalConfig.includeWeekends} 
                                            onChange={handleAdditionalConfigChange('includeWeekends')}
                                            disabled={isDisabled}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: '#10B981',
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: '#10B981',
                                                },
                                                '& .MuiSwitch-track': {
                                                    backgroundColor: '#D1D5DB',
                                                },
                                            }}
                                        />
                                    } 
                                    label="Include weekends in cycle time?" 
                                    sx={{ '& .MuiFormControlLabel-label': { fontWeight: 600 } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel 
                                    control={
                                        <Switch 
                                            checked={cycleTimeConfig.additionalConfig.excludeBlockedTime} 
                                            onChange={handleAdditionalConfigChange('excludeBlockedTime')}
                                            disabled={isDisabled}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: '#10B981',
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: '#10B981',
                                                },
                                                '& .MuiSwitch-track': {
                                                    backgroundColor: '#D1D5DB',
                                                },
                                            }}
                                        />
                                    } 
                                    label="Exclude blocked/paused time?" 
                                    sx={{ '& .MuiFormControlLabel-label': { fontWeight: 600 } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel 
                                    control={
                                        <Switch 
                                            checked={cycleTimeConfig.additionalConfig.aggregateMultiplePRs} 
                                            onChange={handleAdditionalConfigChange('aggregateMultiplePRs')}
                                            disabled={isDisabled}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: '#10B981',
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: '#10B981',
                                                },
                                                '& .MuiSwitch-track': {
                                                    backgroundColor: '#D1D5DB',
                                                },
                                            }}
                                        />
                                    } 
                                    label="Aggregate multiple PRs per ticket?" 
                                    sx={{ '& .MuiFormControlLabel-label': { fontWeight: 600 } }}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>
            
            {/* Cycle Time Action Buttons */}
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Button 
                        variant="outlined" 
                        onClick={handleResetToDefault}
                        disabled={isDisabled}
                          sx={{
                                borderRadius: 2,
                            px: 4,
                            py: 1.5,
                                fontWeight: 600,
                            fontSize: '1rem',
                            borderColor: '#D1D5DB',
                            color: '#6B7280',
                            backgroundColor: '#FFFFFF',
                            '&:hover': {
                                borderColor: '#9CA3AF',
                                backgroundColor: '#F9FAFB',
                                color: '#374151',
                            },
                        }}
                    >
                        Reset to Default
                    </Button>
                <Button 
                    variant="contained" 
                        onClick={handleSaveCycleTimeConfig}
                        disabled={!hasChanges || isDisabled}
                    sx={{
                            borderRadius: 2,
                        px: 4,
                        py: 1.5,
                        fontWeight: 600,
                        fontSize: '1rem',
                            backgroundColor: hasChanges ? '#2563EB' : '#D1D5DB',
                            color: hasChanges ? '#FFFFFF' : '#9CA3AF',
                            boxShadow: hasChanges ? '0 1px 2px rgba(37, 99, 235, 0.2)' : 'none',
                            '&:hover': {
                                backgroundColor: hasChanges ? '#1D4ED8' : '#D1D5DB',
                                boxShadow: hasChanges ? '0 4px 6px rgba(37, 99, 235, 0.3)' : 'none',
                            },
                            '&:disabled': {
                                backgroundColor: '#D1D5DB',
                                color: '#9CA3AF',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        Save & Apply
                </Button>
                </Box>
            </Grid>

        </Grid>
    </Container>
  );
}

export default Settings; 